import React, {memo, useCallback, useEffect, useRef, useState} from 'react';
import { ADMIN_INVOICES_DATE_BYPASS_KEYS } from "../AdminInvoicesQueryType";
import SortFilter from "../../../../SortFilter";
import {useBindSearchParam} from "../../../../../hooks/useBindSearchParam";
import {filterByOptions, filterBySelectOptions, sortByOptions, filterNames} from "./data";
import {Grid} from "@mui/material";
import {AccountType, DateFormats} from "../../../../constants/securspace-constants";
import {AdminCompanyNamesAutocomplete} from "../../../autocompletes/AdminCompanyNamesAutocomplete";
import {getAssetTypeGroupOptionsTypes, getTaxStatusTypes} from "../../../../../requests/type-requests";
import {getErrorMessageForNonStandardAndStandardResponse} from "../../../../../util/NetworkErrorUtil";
import moment from "moment/moment";
import type {AdminInvoicesQueryType} from "../AdminInvoicesQueryType";
import {useSnackbar} from "../../../../../context/snackbar-context";
import type {AutocompleteOption, FilterBySelectProps, SortFilterCallback} from "../../../../SortFilter/interfaces";
import {LabelValue} from "../../../../../types/common/LabelValue";
import type {DateRange} from "../../../../../util/ReportDateRangeUtils";


type InvoiceSortFilterProps = {
  setQuery: React.Dispatch<React.SetStateAction<AdminInvoicesQueryType>>,
  initialQuery: AdminInvoicesQueryType,
  /** Receives the date range and apply-dates override on every change, e.g. to keep them in the URL. */
  onDateRangeChange?: (range: DateRange, applyDates?: boolean) => void,
};

const formatDay = (date?: string) => (date ? moment(date).format(DateFormats.DAY) : '');

/**
 * The select filters with the query's values applied. Returns new objects: the
 * options are module-level, so writing a value into them would carry one visit's
 * filter into the next.
 */
const withQueryValues = (options: FilterBySelectProps[], query: AdminInvoicesQueryType): FilterBySelectProps[] =>
  options.map(filter => {
    // Guaranteed name is a string, but may not be a key on the query, so we need to check if it exists before trying to access it
    const queryValue = (query as Record<string, unknown>)[filter.name];
    return {...filter, value: queryValue ? String(queryValue) : filter.value};
  });

const InvoiceSortFilter = memo(({
  setQuery,
  initialQuery,
  onDateRangeChange,
}: InvoiceSortFilterProps) => {
  const initialRender = useRef(true);
  const [filterBySelectOption, setFilterBySelectOption] = useState<FilterBySelectProps[]>(
    () => withQueryValues(filterBySelectOptions, initialQuery)
  );
  const [assetTypeGroupOptions, setAssetTypeGroupOptions] = useState<LabelValue[]>([]);
  const [taxStatusOptions, setTaxStatusOptions] = useState<LabelValue[]>([]);
  const [buyerAccountId, setBuyerAccountId] = useState<string>(initialQuery.buyerAccountId || '');
  const [supplierAccountId, setSupplierAccountId] = useState<string>(initialQuery.supplierAccountId || '');

  const { snackbarShowMessage } = useSnackbar();

  const [showAllFilters, setShowAllFilters] = useBindSearchParam<'true' | 'false'>('showAllFilters', 'false', {replace: true});
  const filtersExpanded = showAllFilters === 'true';

  const handleExpandedChange = useCallback((expanded: boolean) => {
    setShowAllFilters(expanded ? 'true' : 'false');
  }, [setShowAllFilters]);

  const handleBuyerChange = useCallback((_: React.SyntheticEvent, value: AutocompleteOption | null) => {
    const accountId = value?.id ?? '';
    setBuyerAccountId(accountId);
    setQuery(prevState => ({...prevState, buyerAccountId: accountId}));
  }, [setQuery]);

  const handleSupplierChange = useCallback((_: React.SyntheticEvent, value: AutocompleteOption | null) => {
    const accountId = value?.id ?? '';
    setSupplierAccountId(accountId);
    setQuery(prevState => ({...prevState, supplierAccountId: accountId}));
  }, [setQuery]);

  useEffect(() => {

    const formatFilterOptionResponse = (({body}: {body: LabelValue[]}) => {
      const options: LabelValue[] = [{label: 'All', value: ''}];
      return options.concat(body.map(option => ({label: option.label, value: option.value})))
    });

    const fetchData = async () => {
      try {
        const [assetTypeGroupOptionsResponse, taxStatusResponse] = await Promise.all([
          getAssetTypeGroupOptionsTypes(),
          getTaxStatusTypes(),
        ]);

        setAssetTypeGroupOptions(formatFilterOptionResponse(assetTypeGroupOptionsResponse));
        setTaxStatusOptions(formatFilterOptionResponse(taxStatusResponse));
      } catch (error) {
        snackbarShowMessage(getErrorMessageForNonStandardAndStandardResponse(error), 'error');
      }
    };

    fetchData();
  }, [snackbarShowMessage]);


  useEffect(() => {
    const updateAssetTypeGroupAndTaxStatusOptions = (existingOptions: FilterBySelectProps[], query: AdminInvoicesQueryType)=> {
      const withOptions = existingOptions.map(filter => {
        if (filter.name === filterNames.ASSET_TYPE_GROUP) {
          return {...filter, option: assetTypeGroupOptions};
        }
        if (filter.name === filterNames.TAX_STATUS) {
          return {...filter, option: taxStatusOptions};
        }
        return filter;
      });
      return withQueryValues(withOptions, query);
    };

    if (assetTypeGroupOptions.length && taxStatusOptions.length) {
      setFilterBySelectOption(prev => updateAssetTypeGroupAndTaxStatusOptions(prev, initialQuery));
    }

  }, [initialQuery, assetTypeGroupOptions, taxStatusOptions]);

  const handleCallback = useCallback((e: SortFilterCallback) => {
    if (!initialRender.current) {
      const startDate = formatDay(e.startDate);
      const endDate = formatDay(e.endDate);
      onDateRangeChange?.({startDate, endDate}, e.applyDates);
      setQuery(prevState => {
        return {
          ...prevState,
          bookingNumber: e.filters.find(filter => filter.name === 'bookingNumber')?.value || '',
          endDate,
          startDate,
          applyDates: e.applyDates,
          transactionNumber: e.filters.find(filter => filter.name === 'transactionNumber')?.value || '',
          // Select Filters
          assetTypeGroup: e.selectFilters?.find(filter => filter.name === 'assetTypeGroup')?.value || '',
          bookingStatus: e.selectFilters?.find(filter => filter.name === 'bookingStatus')?.value || '',
          paymentProcessor: e.selectFilters?.find(filter => filter.name === 'paymentProcessor')?.value || '',
          supplierTransactionStatus: e.selectFilters?.find(filter => filter.name === 'supplierTransactionStatus')?.value || '',
          taxStatus: e.selectFilters?.find(filter => filter.name === 'taxStatus')?.value || '',
          transactionType: e.selectFilters?.find(filter => filter.name === 'transactionType')?.value || '',
          sortBy: e.sortBy,
          sortDir: e.sortDir || prevState.sortDir,
        }
      })
    } else {
      initialRender.current = false;
    }
  }, [setQuery, onDateRangeChange]);

  return (
    <Grid item container direction={'column'} component={'section'}>
          <SortFilter
            sortByOption={sortByOptions}
            additionalFilters={
              <>
                <Grid item xs={12} sm={4}>
                  <AdminCompanyNamesAutocomplete
                    accountType={AccountType.BUYER}
                    label={'Buyer'}
                    value={buyerAccountId}
                    onChange={handleBuyerChange}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <AdminCompanyNamesAutocomplete
                    accountType={AccountType.SUPPLIER}
                    label={'Supplier'}
                    value={supplierAccountId}
                    onChange={handleSupplierChange}
                  />
                </Grid>
              </>
            }
            filterByOption={
              filterByOptions.map(filter => {
                return {
                  ...filter,
                  value: (initialQuery as Record<string, unknown>)[filter.name]
                    ? String((initialQuery as Record<string, unknown>)[filter.name])
                    : filter.value
              }})
            }
            filterBySelectOption={filterBySelectOption}
            filterCallback={handleCallback}
            defaultStartDate={initialQuery.startDate}
            defaultEndDate={initialQuery.endDate}
            showDateRangePresets
            dateBypassFilterNames={ADMIN_INVOICES_DATE_BYPASS_KEYS}
            dateBypassLabel="invoice or booking number"
            defaultApplyDates={initialQuery.applyDates}
            additionalFiltersInDateRow
            defaultSortBy={initialQuery.sortBy}
            sortDir={initialQuery.sortDir as 'asc' | 'desc'}
            expandable
            expanded={filtersExpanded}
            onExpandedChange={handleExpandedChange}
          />
    </Grid>
  );
});

InvoiceSortFilter.displayName = 'InvoiceSortFilter';

export default InvoiceSortFilter;
